/**
 * Video player chrome.
 *
 * Play, scrubber, time, and caption are viewer modules (like icon-close), not
 * site-header blocks. Placement and style live on header.modules[id] boxes.
 * An unplaced module stays in the default row under the video.
 */

import { cssLength, moduleBox } from './header-modules.js';
import { colorWithAlpha } from './colors.js';
import { squishScale } from './squish.js';

export const VIDEO_PLAY = 'video-play';
export const VIDEO_SCRUBBER = 'video-scrubber';
export const VIDEO_TIME = 'video-time';
export const VIDEO_CAPTION = 'video-caption';
export const ICON_PLAY = 'icon-play';
export const ICON_PAUSE = 'icon-pause';

export const VIDEO_CHROME_MODULE_IDS = [
    VIDEO_PLAY,
    VIDEO_SCRUBBER,
    VIDEO_TIME,
    VIDEO_CAPTION,
];

export const VIDEO_TIME_FORMATS = [
    { id: 'elapsed/total', label: 'Elapsed / total' },
    { id: 'elapsed', label: 'Elapsed' },
    { id: 'remaining', label: 'Remaining' },
];

export const VIDEO_CASING_OPTIONS = [
    { id: '', label: 'Caption default' },
    { id: 'uppercase', label: 'Uppercase' },
    { id: 'lowercase', label: 'Lowercase' },
    { id: 'none', label: 'As typed' },
];

const SCRUBBER_DEFAULTS = {
    trackColor: '#d4d4d4',
    fillColor: '#404040',
    thickness: 2,
};

const TIME_DEFAULTS = {
    faceId: null,
    size: 0.875,
    color: null,
    format: 'elapsed/total',
};

const CAPTION_DEFAULTS = {
    color: null,
};

const PLAY_DEFAULTS = {
    color: null,
};

/** @param {string} id */
export function isVideoChromeModule(id) {
    return typeof id === 'string' && (
        id.startsWith('video-')
        || id === ICON_PLAY
        || id === ICON_PAUSE
    );
}

/** @param {string} id */
export function videoChromeDefaults(id) {
    if (id === VIDEO_SCRUBBER) return { ...SCRUBBER_DEFAULTS };
    if (id === VIDEO_TIME) return { ...TIME_DEFAULTS };
    if (id === VIDEO_CAPTION) return { ...CAPTION_DEFAULTS };
    if (id === VIDEO_PLAY) return { ...PLAY_DEFAULTS };
    return {};
}

/**
 * Stored box merged with defaults. Missing modules stay unplaced.
 * @param {Object} modules
 * @param {string} id
 * @param {string} [layer]
 */
export function videoChromeBox(modules, id, layer = 'desktop') {
    const stored = moduleBox(modules, id, layer) || {};
    return { ...videoChromeDefaults(id), ...stored };
}

/**
 * Theme with every video player setting back to its default: placement, size,
 * colours, type, and the play/pause glyphs, at every breakpoint.
 * @param {Object} theme
 */
export function resetVideoChrome(theme) {
    const ids = [...VIDEO_CHROME_MODULE_IDS, ICON_PLAY, ICON_PAUSE];
    const modules = { ...(theme?.header?.modules || {}) };
    ids.forEach((id) => { delete modules[id]; });
    const next = { ...theme, header: { ...(theme?.header || {}), modules } };
    if (theme?.overrides) {
        const overrides = {};
        Object.entries(theme.overrides).forEach(([layer, map]) => {
            overrides[layer] = Object.fromEntries(Object.entries(map || {})
                .filter(([path]) => !ids.some((id) => path === `header.modules.${id}` || path.startsWith(`header.modules.${id}.`))));
        });
        next.overrides = overrides;
    }
    return next;
}

function isPlaced(box) {
    return Boolean(box && box.anchor === 'free');
}

function storedSize(value) {
    return value && value !== 'auto' ? value : null;
}

/**
 * Where each control of the row goes. The row is a fixed three-cell grid; a
 * placed control leaves its cell and the cell keeps a stand-in of the same size.
 * @param {{ play?: Object, scrubber?: Object, time?: Object }} stored - stored boxes
 */
export function videoControlsLayout({ play, scrubber, time } = {}) {
    const playPlaced = isPlaced(play);
    const scrubberPlaced = isPlaced(scrubber);
    const timePlaced = isPlaced(time);
    return {
        playPlaced,
        scrubberPlaced,
        timePlaced,
        playWidth: storedSize(play?.w) || '1rem',
        playHeight: storedSize(play?.h) || '1rem',
        playClass: `${playPlaced ? '' : 'relative '}z-[1] inline-flex shrink-0 items-center justify-center`,
        scrubberClass: `z-[1] flex h-5 items-center${scrubberPlaced ? (storedSize(scrubber?.w) ? '' : ' w-48') : ' relative w-full'}`,
        timeClass: `${timePlaced ? '' : 'relative '}z-[1] inline-flex shrink-0 items-center whitespace-nowrap`,
        timeStandIn: timePlaced,
    };
}

/** Scrubber track thickness as a CSS length; bare numbers are px. */
export function videoTrackThickness(box) {
    const value = cssLength(box?.thickness, 'px');
    return value && parseFloat(value) > 0 ? value : '2px';
}

/** Face ids referenced by the video time label, so @font-face still emits. */
export function videoChromeFaceIds(theme) {
    const modules = theme?.header?.modules || {};
    const ids = [];
    [VIDEO_TIME].forEach((id) => {
        const entry = modules[id] || {};
        ['desktop', 'phone', 'tablet'].forEach((layer) => {
            const faceId = entry[layer]?.faceId;
            if (faceId) ids.push(faceId);
        });
    });
    return ids;
}

export function formatClock(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
}

export function videoTimeLabel(current, duration, format) {
    const elapsed = formatClock(current);
    const total = formatClock(duration);
    const remaining = formatClock(Math.max(0, (duration || 0) - (current || 0)));
    if (format === 'elapsed') return elapsed;
    if (format === 'remaining') return remaining;
    return `${elapsed} / ${total}`;
}

/**
 * Viewer title and video caption. Face, size, line height, spacing and casing
 * come from the caption variables so they match the site and block captions;
 * the box sets only colour and background.
 * @param {Object} box
 * @param {Object} theme
 */
export function videoTextStyle(box, theme) {
    const tracking = cssLength(theme?.chrome?.captionTracking, 'em') || '0.15em';
    const casing = theme?.chrome?.captionCasing || 'uppercase';
    const background = colorWithAlpha(
        box.background,
        box.backgroundOpacity != null && box.backgroundOpacity !== '' ? box.backgroundOpacity : 1,
    );
    return {
        fontFamily: 'var(--font-caption, var(--font-nav, "Sansumi", system-ui, sans-serif))',
        fontSize: 'var(--caption-size, 0.875rem)',
        lineHeight: 'var(--caption-leading, normal)',
        fontWeight: 'var(--caption-weight, inherit)',
        fontStyle: 'var(--caption-style, normal)',
        letterSpacing: `var(--caption-tracking, ${tracking})`,
        textTransform: `var(--caption-casing, ${casing === 'none' ? 'none' : casing})`,
        color: box.color || 'var(--color-text)',
        ...(background ? {
            backgroundColor: background,
            padding: '0.35em 0.65em',
        } : {}),
    };
}

export function videoTimeStyle(box, library, resolveFace) {
    const faceId = box.faceId || null;
    const fontFamily = faceId
        ? (resolveFace(faceId, library).css || 'inherit')
        : 'inherit';
    return {
        fontFamily,
        fontSize: cssLength(box.size, 'rem') || '0.875rem',
        fontWeight: box.weight || undefined,
        fontStyle: box.style === 'italic' ? 'italic' : undefined,
        letterSpacing: box.tracking != null && box.tracking !== '' ? cssLength(box.tracking, 'em') : undefined,
        scale: squishScale(box.squish),
        color: box.color || 'rgba(0,0,0,0.6)',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
    };
}
